import { inject, Service } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { KeyValueDataService } from '../database/services/key-value-data.service';
import { QuestionSectionDataService } from '../database/services/question-section-data.service';
import { CandidateQuestionDto } from '../model/exam/candidate-question-dto';
import { QuestionSectionDto } from '../model/exam/question-section-dto';

export enum QuestionAccess {
	Open,
	Blocked,
	NeedsSectionMove
}

@Service()
export class QuestionAccessService {

	private readonly keyValueDataService: KeyValueDataService = inject(KeyValueDataService);
	private readonly questionSectionDataService: QuestionSectionDataService = inject(QuestionSectionDataService);

	/**
	 * Decides what the exam is permitted to do with a question.
	 *
	 * A later section always asks the candidate to move on. Everything else comes from the
	 * earliest section that the candidate can still open, which
	 * `getFirstReachableSectionNumber` works out from the shared timer and the walls between
	 * the sections.
	 */
	public resolve(question?: CandidateQuestionDto): Observable<QuestionAccess> {
		if (!question) {
			return of(QuestionAccess.Blocked);
		}

		return forkJoin({
			section: this.questionSectionDataService.get(question.sectionId).pipe(first()),
			currentSectionNumber: this.keyValueDataService.getCurrentSectionNumber().pipe(first()),
			firstReachableSectionNumber: this.questionSectionDataService.getFirstReachableSectionNumber().pipe(first())
		})
		.pipe(map((data: { section: QuestionSectionDto, currentSectionNumber: number, firstReachableSectionNumber: number }) => {
			if (data.section.orderIndex > data.currentSectionNumber) {
				return QuestionAccess.NeedsSectionMove;
			}

			return data.section.orderIndex >= data.firstReachableSectionNumber
				? QuestionAccess.Open
				: QuestionAccess.Blocked;
		}));
	}
}
